import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Avatar,
  Divider,
  Skeleton,
  IconButton
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from '@mui/icons-material/X';

import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { supabase } from "../Supabase/supabaseClient";
import StickyFooter from "../DevopsV2/components/StickyFooter";
import Footer from "../DevopsV2/components/Footer";
import Navbar from "../DevopsV2/components/Navbar";
import ErrorPage from "../components/ErrorPage";
import { batchDetailsForExectivePG } from "../constants/External";
import RecommendedCourseImg from "./recommended-course.jpg";
import FallbackCircuitBoard from "./images/fallback-circuit-board.webp";
import FallbackCodeScreen from "./images/fallback-code-screen.webp";
import FallbackDevWorkstation from "./images/fallback-dev-workstation.webp";
import FallbackTechOffice from "./images/fallback-tech-office.webp";
import TalkToOurConsultantPopupV3 from "../DevopsV3/components/Popup/TalkToOurConsultantPopupV3";
import DynamicBlogCard from "./DynamicBlogCard";

const COLORS = {
  ink: "#0e1320",
  muted: "#5b6376",
  border: "#e6e8f1",
  soft: "#f5f6fb",
  purple: "#6c2bd9",
  purpleDark: "#4a1db0",
  orange: "#ff6b35"
};

const MONO_FONT = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const POPULAR_COURSES = [
  {
    title: "Online DevOps Course · Multi-Cloud (AWS, Azure, GCP) · Gen AI — Job-Ready Program",
    desc: "Master CI/CD, Docker, Kubernetes, Terraform and Gen AI with hands-on projects across AWS, Azure and GCP — and become job-ready with dedicated placement support.",
    cta: "Learn More",
    link: "https://paperlive.in/devops-training-online?utm_source=Blog&utm_medium=recommended+course&utm_campaign=organic"
  },
];

const ZOHO_ACTION_URL = "https://crm.zoho.in/crm/WebToLeadForm";
const ZOHO_XNQSJSDP = "687200ebfd3b2fe7528be8fc456683865bab8cc38280d6ff4dda9352f2525ee8";
const ZOHO_XMIWTLD = "cc543c4baa5e86451f2b99c193e1fc869fb07de37de357f307632b791e840601f5a20e4d91f34cf35597c60ad50dac4c";
const ZOHO_FORM_NAME = "webToLeadsBlogCurriculumForm";
const ZOHO_RETURN_URL = "https://paperlive.in/devops-training-online?utm_source=Blog&utm_medium=recommended+course&utm_campaign=organic";

function validateName(v) {
  return v.trim() ? "" : "Please enter your name";
}
function validateEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "" : "Please enter a valid email";
}
function validatePhone(v) {
  return /^\d{10}$/.test(v) ? "" : "Please enter a valid 10-digit phone number";
}

// Split a full name into first / last for the Zoho lead form.
const splitName = (full) => {
  const parts = full.trim().split(/\s+/);
  const firstName = parts.shift() || "";
  const lastName = parts.join(" ");
  return { firstName, lastName };
};

function CurriculumForm() {
  const [formValues, setFormValues] = useState({ name: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [gclid, setGclid] = useState("");

  useEffect(() => {
    const urlGclid = new URLSearchParams(window.location.search).get("gclid");
    if (urlGclid) {
      localStorage.setItem("gclid", urlGclid);
      setGclid(urlGclid);
    } else {
      setGclid(localStorage.getItem("gclid") || "");
    }
  }, []);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));

    let err = "";
    if (field === "name") err = validateName(value);
    if (field === "email") err = validateEmail(value);
    if (field === "phone") err = validatePhone(value);

    setErrors((prev) => ({ ...prev, [field]: err }));
  };

  // No manual submit — only block invalid forms, otherwise let it POST to Zoho.
  const handleSubmit = (e) => {
    const nameError = validateName(formValues.name);
    const phoneError = validatePhone(formValues.phone);
    const emailError = validateEmail(formValues.email);

    if (nameError || phoneError || emailError) {
      e.preventDefault();
      setErrors({ name: nameError, phone: phoneError, email: emailError });
      setFormError(nameError || phoneError || emailError);
    }
    // else: allow browser to submit normally to Zoho
  };

  const { firstName, lastName } = splitName(formValues.name);

  const inputSx = {
    width: "100%",
    padding: "12px 13px",
    border: `1.5px solid ${COLORS.border}`,
    borderRadius: "10px",
    fontSize: 13.5,
    fontFamily: "'Poppins', sans-serif",
    outline: "none",
    boxSizing: "border-box"
  };

  return (
    <Box
      id="apply"
      sx={{
        background: "#fff",
        borderRadius: "16px",
        boxShadow: "0 18px 50px -18px rgba(13,18,40,.28)",
        overflow: "hidden",
        border: `1px solid ${COLORS.border}`
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 14.5, color: COLORS.ink, mb: 0.3 }}>
          Get the curriculum
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: COLORS.muted, mb: 1.75 }}>
          DevOps Course Online
        </Typography>

        <Box
          component="form"
          id="webform_blog_curriculum"
          name={ZOHO_FORM_NAME}
          method="POST"
          action={ZOHO_ACTION_URL}
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
          <input type="hidden" name="xnQsjsdp" defaultValue={ZOHO_XNQSJSDP} />
          <input type="hidden" name="xmIwtLD" defaultValue={ZOHO_XMIWTLD} />
          <input type="hidden" name="actionType" defaultValue="TGVhZHM=" />
          <input type="hidden" name="returnURL" defaultValue={ZOHO_RETURN_URL} />
          <input type="hidden" id="zc_gad" name="zc_gad" value={gclid} />
          <input type="hidden" name="Website" value={window.location.href.slice(0, 250)} />
          <input type="hidden" name="Description" value="Blog — Get the curriculum" />
          <input type="hidden" name="First Name" value={firstName} />
          <input type="hidden" name="Last Name" value={lastName} />

          <input
            type="text"
            placeholder="Full name"
            value={formValues.name}
            onChange={handleChange("name")}
            style={inputSx}
          />
          {errors.name && (
            <Typography sx={{ fontSize: 11.5, color: "#c0392b" }}>{errors.name}</Typography>
          )}

          <input
            type="tel"
            name="Phone"
            placeholder="Phone (WhatsApp preferred)"
            value={formValues.phone}
            onChange={handleChange("phone")}
            style={inputSx}
          />
          {errors.phone && (
            <Typography sx={{ fontSize: 11.5, color: "#c0392b" }}>{errors.phone}</Typography>
          )}

          <input
            type="email"
            name="Email"
            placeholder="Email address"
            value={formValues.email}
            onChange={handleChange("email")}
            style={inputSx}
          />
          {errors.email && (
            <Typography sx={{ fontSize: 11.5, color: "#c0392b" }}>{errors.email}</Typography>
          )}

          {formError && (
            <Typography sx={{ fontSize: 11.5, color: "#c0392b" }}>{formError}</Typography>
          )}

          <Box
            component="button"
            type="submit"
            sx={{
              width: "100%",
              background: COLORS.orange,
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              padding: "13px",
              border: "none",
              borderRadius: "10px",
              fontFamily: "'Poppins', sans-serif",
              cursor: "pointer",
              boxShadow: "0 10px 26px -10px rgba(255,107,53,.7)"
            }}
          >
            Get Curriculum →
          </Box>
        </Box>

        <Typography sx={{ textAlign: "center", fontSize: 11, color: COLORS.muted, mt: 1.25 }}>
          100% secure · no spam · callback in 30 min
        </Typography>
      </Box>
    </Box>
  );
}

const BLOG_META_ID = "__blog_meta__";
const ARTICLE_CONTENT_ID = "blog-article-content";

// Deterministic so the prerendered (server) pick and the client's first
// render always agree — Math.random() here would pick a different image on
// each side and break hydration.
function pickStableImage(images, seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return images[Math.abs(hash) % images.length];
}

// Prerendered blog pages already contain the full article in the DOM. Seed
// state from that instead of starting at null, so the client's first render
// matches the server HTML exactly and hydration doesn't have to discard and
// rebuild the page (that discard is what caused the CLS regression).
// `content` itself isn't duplicated in the JSON blob — it's read straight
// back out of the already-rendered DOM, keeping the embed small.
function readEmbeddedBlogMeta(slug) {
  if (typeof document === "undefined") return null;
  const metaEl = document.getElementById(BLOG_META_ID);
  const contentEl = document.getElementById(ARTICLE_CONTENT_ID);
  if (!metaEl || !contentEl) return null;

  try {
    const parsed = JSON.parse(metaEl.textContent || "{}");
    if (!parsed.blog || parsed.blog.slug !== slug) return null;
    return { blog: parsed.blog, toc: parsed.toc || [], contentWithIds: contentEl.innerHTML };
  } catch {
    return null;
  }
}

export default function BlogLayout() {
  const isSmallScreen = window.innerWidth < 600;
  const { slug } = useParams();

  const images = [
    FallbackCircuitBoard,
    FallbackCodeScreen,
    FallbackDevWorkstation,
    FallbackTechOffice
  ];

  const selectedImage = pickStableImage(images, slug);

  const [embeddedSeed] = useState(() => readEmbeddedBlogMeta(slug));
  const [blog, setBlog] = useState(embeddedSeed?.blog || null);
  const [notFound, setNotFound] = useState(false);
  const [toc, setToc] = useState(embeddedSeed?.toc || []);
  const [contentWithIds, setContentWithIds] = useState(embeddedSeed?.contentWithIds || "");
  const [activeSection, setActiveSection] = useState("");
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [consultantPopupOpen, setConsultantPopupOpen] = useState(false);

  const firstRunRef = useRef(true);

  useEffect(() => {
    const isSeeded = firstRunRef.current && embeddedSeed?.blog?.slug === slug;
    firstRunRef.current = false;
    fetchBlog(isSeeded);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = toc.map(item =>
        document.getElementById(item.id)
      );

      let current = "";

      sections.forEach(section => {
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 120) {
            current = section.id;
          }
        }
      });

      if (current) setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [toc]);

  const fetchBlog = async (silent = false) => {
    // A seeded mount already has the article on screen (from the prerendered
    // HTML) — don't drop it back to the loading skeleton while revalidating.
    if (!silent) {
      setBlog(null);
      setNotFound(false);
    }

    const { data, error } = await supabase
      .from("blogs")
      .select("*")
      .eq("slug", slug)
      .single();

    if (!error && data) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(data.content, "text/html");

      // ✅ Remove Quill's empty placeholder paragraphs (<p><br></p>)
      // that create large blank gaps, especially in pasted content
      doc.querySelectorAll("p").forEach((p) => {
        if (!p.textContent.trim() && !p.querySelector("img")) {
          p.remove();
        }
      });

      // ✅ TOC generation
      const headings = doc.querySelectorAll("h2");
      const tocList = [];

      headings.forEach((h, index) => {
        const id = `section-${index}`;
        h.setAttribute("id", id);

        tocList.push({
          id,
          text: h.innerText,
          tag: h.tagName
        });
      });

      // ✅ Fix links (important)
      const links = doc.querySelectorAll("a");
      links.forEach(link => {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      });

      // ✅ Insert "Ready to become a DevOps engineer?" CTA right after the
      // 2nd Table of Contents section (before the 3rd h2, or at the end
      // if the article has 2 or fewer sections)
      const ctaWrapper = doc.createElement("div");
      ctaWrapper.innerHTML = `
        <div style="background:linear-gradient(135deg, ${COLORS.purple}, ${COLORS.purpleDark});border-radius:16px;padding:30px 32px;color:#fff;display:flex;align-items:center;gap:24px;flex-wrap:wrap;margin:32px 0 40px;max-width:680px">
          <div style="flex:1;min-width:220px">
            <div style="font-family:'Poppins',sans-serif;font-weight:700;font-size:19px;margin-bottom:6px">Ready to become a DevOps engineer?</div>
            <div style="font-size:13.5px;opacity:.85">Industry-certified DevOps training online · 100% job-opportunity guarantee</div>
          </div>
          <a href="https://paperlive.in/devops-training-online?utm_source=Blog&utm_medium=in_Between_Promotion&utm_campaign=organic" id="cta-get-curriculum" style="background:${COLORS.orange};color:#fff;font-weight:700;font-size:14.5px;padding:14px 24px;border-radius:11px;white-space:nowrap;box-shadow:0 10px 26px -10px rgba(255,107,53,.7);text-decoration:none;display:inline-block">Get Curriculum →</a>
        </div>
      `.trim();
      const ctaNode = ctaWrapper.firstElementChild;

      if (headings.length >= 3) {
        headings[2].parentNode.insertBefore(ctaNode, headings[2]);
      } else {
        doc.body.appendChild(ctaNode);
      }

      // A silent (seeded) revalidation whose fetched row hasn't actually
      // changed since the prerendered snapshot would otherwise still replace
      // the already-on-screen content with a freshly-reserialized (but
      // visually identical) copy on every single load — a guaranteed,
      // pointless full-article DOM replace and layout shift. Skip the write
      // when nothing changed; still update if the article was edited.
      const unchanged =
        silent &&
        blog &&
        blog.slug === data.slug &&
        (blog.updated_at || blog.created_at) === (data.updated_at || data.created_at);

      if (!unchanged) {
        setToc(tocList);
        setContentWithIds(doc.body.innerHTML);
        setBlog(data);
      }
      fetchRelatedBlogs(data.slug);
    } else if (!silent) {
      setNotFound(true);
    }
  };

  const fetchRelatedBlogs = async (currentSlug) => {
    const { data, error } = await supabase
      .from("blogs")
      .select("title, slug, featured_image, created_at")
      .neq("slug", currentSlug)
      .order("created_at", { ascending: false })
      .limit(3);

    if (!error && data) {
      setRelatedBlogs(data);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  if (notFound) {
    return <ErrorPage />;
  }

  if (!blog) {
    return (
      <>
        <Navbar isSmallScreen={isSmallScreen} />

        {/* BREADCRUMB SKELETON */}
        <Box sx={{ borderBottom: "1px solid #eee" }}>
          <Container>
            <Box py={2}>
              <Skeleton variant="text" animation="wave" width={260} />
            </Box>
          </Container>
        </Box>

        {/* CONTENT SKELETON */}
        <Container sx={{ mt: 4, mb: 10 }}>
          <Skeleton variant="text" animation="wave" width="65%" height={56} sx={{ mb: 1 }} />
          <Skeleton variant="text" animation="wave" width={220} sx={{ mb: 4 }} />
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{ mb: 5, borderRadius: 3, height: { xs: 220, md: 420 } }}
          />

          <Grid container spacing={6}>
            <Grid item xs={12} md={8}>
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={`p-${i}`} variant="text" animation="wave" width={i % 4 === 3 ? "70%" : "100%"} />
              ))}
              <Skeleton variant="rounded" animation="wave" height={260} sx={{ my: 4, borderRadius: 2 }} />
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={`q-${i}`} variant="text" animation="wave" width={i % 3 === 2 ? "65%" : "100%"} />
              ))}
            </Grid>

            <Grid item xs={12} md={4}>
              {!isSmallScreen && (
                <Box sx={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 2, p: 3 }}>
                  <Skeleton variant="text" animation="wave" width={150} height={28} sx={{ mb: 2 }} />
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={`t-${i}`} variant="text" animation="wave" width={`${90 - i * 8}%`} sx={{ mb: 1 }} />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </>
    );
  }

  const faqSchema = blog.faqs && blog.faqs.length > 0
    ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": blog.faqs
        .filter(faq => faq.question && faq.answer)
        .map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer.replace(/<[^>]+>/g, "")
          }
        }))
    }
    : null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blog.title,
    "description": blog.meta_description,
    "image": blog.featured_image || selectedImage,
    "author": {
      "@type": "Person",
      "name": blog.author_name || "Roshan K",
      "url": "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Paperlive Learning",
      "logo": {
        "@type": "ImageObject",
        "url": "https://paperlive.in/static/media/paperlive-new-logo.e507bfd60cf257e2a268.webp"
      }
    },
    "datePublished": new Date(blog.created_at).toISOString(),
    "dateModified": new Date(blog.updated_at || blog.created_at).toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://paperlive.in/blog/${blog.slug}`
    }
  };

  const plainText = contentWithIds.replace(/<[^>]+>/g, " ");
  const wordCount = plainText.trim().split(/\s+/).filter(Boolean).length;
  const readTime = Math.max(1, Math.round(wordCount / 200));

  const shareUrl = typeof window !== "undefined" ? window.location.href : `https://paperlive.in/blog/${blog.slug}`;
  const linkedInShareUrl = blog.author_linkedin_url || "https://www.linkedin.com/company/paperlive-learning/";
  const xShareUrl = `https://x.com/intent/post?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blog.title)}`;

  return (
    <>
      <Navbar isSmallScreen={isSmallScreen} />
      <StickyFooter />

      <Box sx={{ background: "#fff", color: COLORS.ink, marginBottom: 5 }}>
        <Helmet>
          <title>{blog.title} | Paperlive</title>
          <meta name="description" content={blog.meta_description} />

          {faqSchema && (
            <script type="application/ld+json">
              {JSON.stringify(faqSchema)}
            </script>
          )}

          <script type="application/ld+json">
            {JSON.stringify(articleSchema)}
          </script>

          {/* Seeds the client's first render on hydration — see readEmbeddedBlogMeta above. */}
          <script type="application/json" id={BLOG_META_ID}>
            {JSON.stringify({
              blog: {
                slug: blog.slug,
                title: blog.title,
                meta_description: blog.meta_description,
                featured_image: blog.featured_image,
                author_name: blog.author_name,
                author_avatar: blog.author_avatar,
                author_description: blog.author_description,
                author_experience_years: blog.author_experience_years,
                author_readers: blog.author_readers,
                author_linkedin_url: blog.author_linkedin_url,
                created_at: blog.created_at,
                updated_at: blog.updated_at,
                faqs: blog.faqs
              },
              toc
            })}
          </script>
        </Helmet>

        {/* BREADCRUMB */}
        <Container>
          <Box
            sx={{
              fontSize: 13,
              color: COLORS.muted,
              letterSpacing: "0.02em",
              pt: 3,
              pb: 2.5,
              borderBottom: `1px solid ${COLORS.border}`
            }}
          >
            <a href="/" style={{ color: COLORS.muted, textDecoration: "none" }}>HOME</a> /{" "}
            <a href="https://paperlive.in/blog" style={{ color: COLORS.muted, textDecoration: "none" }}>BLOG</a> /{" "}
            <span>{blog.title.toUpperCase()}</span>
          </Box>
        </Container>

        {/* ARTICLE HEADER */}
        <Container sx={{ pt: 2.5 }}>
          <Box sx={{ maxWidth: 760 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                fontSize: { xs: "1.7rem", md: "2.4rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.18,
                mb: 2.25,
                color: COLORS.ink
              }}
            >
              {blog.title}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%", flexWrap: "wrap", rowGap: 1.5, pb: 3, borderBottom: `1px solid ${COLORS.border}` }}
            >
              <Box
                component="a"
                href={`/blog/author/${encodeURIComponent(blog.author_name || "Paperlive")}`}
                sx={{ display: "flex", alignItems: "center", gap: 1.75, textDecoration: "none", color: "inherit" }}
              >
                <Avatar
                  src={blog.author_avatar || "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"}
                  sx={{ width: 44, height: 44, flexShrink: 0 }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: COLORS.ink }}>
                    {blog.author_name || "Paperlive"}
                  </Typography>
                  <Typography sx={{ fontFamily: MONO_FONT, fontSize: 11.5, color: COLORS.muted }}>
                    {new Date(blog.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · {readTime} min read
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1.25 }}>
                <IconButton
                  component="a"
                  href={linkedInShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  sx={{ width: 34, height: 34, borderRadius: "9px", background: COLORS.soft, color: COLORS.muted }}
                >
                  <LinkedInIcon sx={{ fontSize: 17 }} />
                </IconButton>
                {/* <IconButton
                  component="a"
                  href={xShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ width: 34, height: 34, borderRadius: "9px", background: COLORS.soft, color: COLORS.muted }}
                >
                  <XIcon sx={{ fontSize: 16 }} />
                </IconButton> */}
              </Box>
            </Stack>
          </Box>

          {/* HERO IMAGE */}
          <Box
            component="img"
            src={blog.featured_image || selectedImage}
            alt={blog.title}
            sx={{
              display: "block",
              width: "100%",
              height: { xs: 220, md: 420 },
              objectFit: "cover",
              borderRadius: "18px",
              my: { xs: 3.5, md: 5.5 }
            }}
          />

          <Grid container spacing={7}>
            {/* BLOG CONTENT */}
            <Grid item xs={12} md={8}>
              <Box
                id={ARTICLE_CONTENT_ID}
                sx={{
                  maxWidth: 680,
                  fontSize: "16.5px",
                  lineHeight: 1.7,
                  color: "#333c4d",

                  "& p": { margin: "0 0 1.1rem" },
                  "& p:empty": { display: "none" },
                  "& h2, & h3": {
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                    color: COLORS.ink,
                    marginTop: "2.2rem",
                    marginBottom: "0.9rem",
                    lineHeight: 1.3,
                    letterSpacing: "-0.01em"
                  },
                  "& h2": { fontSize: "1.6rem" },
                  "& h3": { fontSize: "1.3rem" },
                  "& ul, & ol": {
                    marginTop: 0,
                    marginBottom: "1.1rem",
                    paddingLeft: "22px",
                    display: "grid",
                    gap: "10px"
                  },
                  "& blockquote": {
                    margin: "1.8rem 0",
                    padding: "18px 22px",
                    borderLeft: `3px solid ${COLORS.purple}`,
                    background: "#f3edff",
                    borderRadius: "0 12px 12px 0",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 600,
                    fontSize: "17px",
                    color: COLORS.ink
                  },
                  "& a": {
                    color: COLORS.purple,
                    textDecoration: "underline",
                    fontWeight: 600
                  },
                  "& a:hover": {
                    color: COLORS.purpleDark
                  },
                  "& img": {
                    maxWidth: "100%",
                    // Quill-authored HTML rarely carries width/height attrs, so the
                    // browser has no intrinsic size to reserve before the image loads.
                    // aspect-ratio keeps modern browsers stable; the px height is a
                    // fallback for react-snap's puppeteer (Chromium ~77, predates
                    // aspect-ratio support) which silently drops that declaration when
                    // it serializes the prerendered stylesheet, so without a height
                    // fallback the static HTML users first paint has no reserved space.
                    height: { xs: 191, sm: 298, md: 382 },
                    aspectRatio: "16/9",
                    borderRadius: 12,
                    margin: "20px 0",
                    objectFit: "cover",
                    display: "block"
                  },
                  "& iframe, & .ql-video": {
                    display: "block",
                    width: "90%",
                    height: { xs: 191, sm: 298, md: 382 },
                    aspectRatio: "16/9",
                    border: 0,
                    borderRadius: 8,
                    margin: "24px auto"
                  },
                  "& pre, & code": {
                    fontFamily: MONO_FONT
                  }
                }}
                dangerouslySetInnerHTML={{
                  __html: contentWithIds
                }}
              />

              {/* AUTHOR */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 3,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "14px",
                  maxWidth: 680
                }}
              >
                <Box
                  component="a"
                  href={`/blog/author/${encodeURIComponent(blog.author_name || "Paperlive")}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                    minWidth: 0,
                    textDecoration: "none",
                    color: "inherit"
                  }}
                >
                  <Avatar
                    sx={{ width: 96, height: 96, flexShrink: 0 }}
                    src={
                      blog.author_avatar ||
                      "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
                    }
                  />

                  <Box>
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 15, color: COLORS.ink }}>
                      {blog.author_name || "Paperlive"}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: COLORS.muted }}>
                      {blog.author_description ||
                        "Passionate about sharing knowledge and insights on technology."}
                    </Typography>
                  </Box>
                </Box>

                <IconButton
                  component="a"
                  href={blog.author_linkedin_url || "https://www.linkedin.com/company/paperlive-learning/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  sx={{ width: 36, height: 36, borderRadius: "9px", background: COLORS.soft, color: "#0A66C2", flexShrink: 0 }}
                >
                  <LinkedInIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Grid>

            {/* SIDEBAR */}
            <Grid item xs={12} md={4}>
              {/* TOC + form move together as one sticky block. */}
              <Box
                sx={{
                  position: "sticky",
                  top: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5
                }}
              >
                {toc.length > 0 && !isSmallScreen && (
                  <Box
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: "14px",
                      p: "18px 20px"
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: MONO_FONT,
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: COLORS.muted,
                        mb: 1.5
                      }}
                    >
                      Table of Contents
                    </Typography>

                    <Stack spacing={1.1}>
                      {toc.map((item, index) => (
                        <Typography
                          key={index}
                          onClick={() => scrollToSection(item.id)}
                          sx={{
                            cursor: "pointer",
                            fontSize: 11.5,
                            color:
                              activeSection === item.id
                                ? COLORS.purple
                                : "#333c4d",
                            fontWeight:
                              activeSection === item.id ? 400 : 400
                          }}
                        >
                          {index + 1}. {item.text}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                <CurriculumForm />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* RECOMMENDED COURSE */}
      <Box
        sx={{
          background: COLORS.soft,
          px: { xs: "20px", sm: "40px" },
          py: { xs: "40px", sm: "64px" },
          fontFamily: "'Inter', sans-serif"
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: { xs: "28px", sm: "32px" },
            color: COLORS.ink,
            m: "0 0 32px",
            letterSpacing: "-0.5px"
          }}
        >
          Recommended Course
        </Typography>

        <Box
          onClick={() => window.open(POPULAR_COURSES[0].link, "_self")}
          sx={{
            maxWidth: "1320px",
            mx: "auto",
            background: "#fff",
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 18px 50px -18px rgba(13,18,40,.22)",
            display: "grid",
            // Responsive breakpoint (not the isSmallScreen JS boolean): react-snap's
            // prerender browser renders at a narrow viewport, so a JS-computed
            // column choice bakes the mobile layout into the static HTML even on
            // this desktop page; hydration then snaps it to two columns, a ~400px
            // CLS jump. A real CSS media query is evaluated by each browser at
            // paint time and can't mismatch between prerender and hydration.
            gridTemplateColumns: { xs: "1fr", sm: "340px minmax(0,1fr)" },
            cursor: "pointer"
          }}
        >
          {/* LEFT: image panel with purple gradient overlay */}
          <Box
            sx={{
              position: "relative",
              p: "28px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflow: "hidden",
              minHeight: { xs: "280px", sm: "auto" }
            }}
          >
            <Box
              component="img"
              src={RecommendedCourseImg}
              alt="Online DevOps course"
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                zIndex: 0
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 1,
                pointerEvents: "none",
                background:
                  `linear-gradient(160deg,${COLORS.purple} 0%,${COLORS.purpleDark} 55%,rgba(14,19,32,0.92) 100%)`
              }}
            />

            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                pointerEvents: "none",
                display: "flex",
                flexDirection: "column",
                flex: 1
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "999px",
                  padding: "6px 14px",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "11px",
                  fontFamily: MONO_FONT,
                  letterSpacing: "0.06em",
                  alignSelf: "flex-start"
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: COLORS.orange
                  }}
                />
                BESTSELLER
              </Box>

              <Box sx={{ flex: 1 }} />

              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  mb: "20px"
                }}
              >
                {["AWS", "Azure", "GCP", "Gen AI"].map((tag) => (
                  <Box
                    key={tag}
                    component="span"
                    sx={{
                      background: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 700,
                      borderRadius: "8px",
                      padding: "5px 11px"
                    }}
                  >
                    {tag}
                  </Box>
                ))}
              </Box>

              <Box>
                <Box
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                    fontSize: "1.6rem",
                    lineHeight: 1.15,
                    color: "#fff"
                  }}
                >
                  Online DevOps Course
                </Box>
                <Box
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                    fontSize: "1.6rem",
                    color: COLORS.orange,
                    lineHeight: 1.15
                  }}
                >
                  Job-Ready Program
                </Box>
              </Box>
            </Box>
          </Box>

          {/* RIGHT: content */}
          <Box
            sx={{
              p: { xs: "28px 22px", sm: "30px 32px" },
              display: "flex",
              flexDirection: "column",
              gap: "20px"
            }}
          >
            <Typography
              component="p"
              sx={{ m: 0, fontSize: "15.5px", lineHeight: 1.65, color: "#333c4d", maxWidth: 600 }}
            >
              Learn from{" "}
              <Box component="strong" sx={{ color: COLORS.ink }}>
                Microsoft-certified experts
              </Box>{" "}
              with real projects, internship certification and dedicated placement
              support to help you land your next DevOps role.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isSmallScreen
                  ? "repeat(2,minmax(0,1fr))"
                  : "repeat(4,minmax(0,1fr))",
                rowGap: isSmallScreen ? "18px" : 0,
                gap: "20px",
                borderTop: `1px solid ${COLORS.border}`,
                borderBottom: `1px solid ${COLORS.border}`,
                py: "20px"
              }}
            >
              {[
                { value: "6 Months", label: "Duration" },
                { value: "5–45 LPA", label: "Opportunity range" },
                { value: "300+ Hrs", label: "Live sessions" },
                { value: "IIT Patna", label: "Certification" }
              ].map((stat) => (
                <Box key={stat.label} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 700,
                      fontSize: "1.15rem",
                      color: COLORS.purple,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {stat.value}
                  </Box>
                  <Box sx={{ fontSize: "12px", color: COLORS.muted, mt: "4px" }}>
                    {stat.label}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "14px",
                flexWrap: "wrap",
                background: "#fdf1e8",
                borderRadius: "12px",
                padding: "12px 16px"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {!isSmallScreen && <Box
                  component="span"
                  sx={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: COLORS.orange,
                    flexShrink: 0
                  }}
                />}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: COLORS.ink, fontSize: "13.5px" }}
                >
                  Next batch closing soon — limited seats
                </Box>
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: MONO_FONT,
                  color: COLORS.muted,
                  fontSize: "12.5px"
                }}
              >
                Live classes · {batchDetailsForExectivePG[1].slice(0,6)}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                flexWrap: "wrap"
              }}
            >
              <Box
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    "https://paperlive.in/devops-training-online?utm_source=Blog&utm_medium=recommended+course&utm_campaign=organic",
                    "_self"
                  );
                }}
                sx={{
                  background: COLORS.orange,
                  color: "#fff",
                  border: "none",
                  borderRadius: "11px",
                  padding: "14px 24px",
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: "14.5px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  boxShadow: "0 10px 26px -10px rgba(255,107,53,.7)"
                }}
              >
                Learn more <span>→</span>
              </Box>
              <Box
                component="a"
                href={POPULAR_COURSES[0].link}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontWeight: 700,
                  fontSize: "14px",
                  color: COLORS.purple,
                  textDecoration: "underline"
                }}
              >
                View full curriculum
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* RELATED ARTICLES */}
      {relatedBlogs.length > 0 && (
        <Container sx={{ mt: 8, mb: 8 }}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: "1.7rem",
              color: COLORS.ink,
              mb: 3.25
            }}
          >
            Related articles
          </Typography>

          <Grid container spacing={2.5}>
            {relatedBlogs.map((rb) => (
              <Grid item xs={12} sm={6} md={4} key={rb.slug}>
                <DynamicBlogCard blog={rb} isSmallScreen={isSmallScreen} />
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      <Footer isSmallScreen={isSmallScreen} />

      <TalkToOurConsultantPopupV3
        open={consultantPopupOpen}
        onClose={() => setConsultantPopupOpen(false)}
      />
    </>
  );
}